import { useEffect } from 'react';
import PublicFooter from '../../components/PublicFooter';
import PublicHeader from '../../components/PublicHeader';
import { Link } from '../../lib/router';

const articles = [
  {
    slug: 'why-customers-visit-a-business-once-and-never-come-back',
    title: 'Why Customers Visit a Business Once and Never Come Back',
    summary:
      'Eight common reasons a positive first experience still fails to become a second visit — and what local businesses can look for.',
  },
  {
    slug: 'how-small-businesses-can-build-more-repeat-customers-without-discounting-everything',
    title: 'How Small Businesses Can Build More Repeat Customers Without Discounting Everything',
    summary:
      'Practical ways to build habits, recognition and reasons to return without relying on constant price cuts.',
  },
];

const articleContent = {
  [articles[0].slug]: {
    ...articles[0],
    intro: [
      'A customer can have a perfectly good experience with your business and still never return. Nothing necessarily went wrong. The product was good, the service was fine, and the customer seemed satisfied. But “good” does not always turn into a second visit.',
      'For many local businesses, repeat business comes down to whether the first experience gave customers a strong enough reason to choose the same business again. Here are some of the most common reasons customers visit once and never come back.',
    ],
    sections: [
      {
        title: 'The experience was good, but forgettable',
        paragraphs: [
          'Not every business needs to be dramatically different, but customers usually need something they can remember.',
          'Imagine two cafes. At the first, the coffee is decent, the food is decent, and the service is fine. At the second, the customer discovers a burnt cheesecake they really like and later tells a friend about it.',
          'Both visits may have been positive, but the second cafe has given the customer something specific to remember.',
          'That memorable element could be:',
        ],
        list: [
          'A standout product',
          'Particularly warm service',
          'A distinctive atmosphere',
          'Exceptional convenience',
          'Great value for money',
          'A product that is difficult to find elsewhere',
        ],
        callout: 'What would a customer remember about us a week after their visit?',
        after: ['If the answer is unclear, the experience may need a stronger point of distinction.'],
      },
      {
        title: 'There was no reason to return soon',
        paragraphs: [
          'Customers often intend to return to businesses they like, but then life gets in the way.',
          'Someone may enjoy a new cafe on Saturday, then spend the next few weekends trying other places. A month later, they may barely remember the cafe existed.',
          'Giving customers a reason to return can help turn vague intention into action. For example:',
        ],
        list: [
          'A cafe could introduce a seasonal drink or weekend-only pastry',
          'A salon could encourage customers to book their next appointment before leaving',
          'A fitness studio could invite a first-time visitor to try another class',
          'A retailer could launch a small seasonal collection',
          'A business could offer a reward that applies on the next visit',
        ],
        after: [
          'The point is not necessarily to discount. It is simply to give customers a reason to come back instead of relying on them to remember you.',
        ],
      },
      {
        title: 'There is nothing new to discover',
        paragraphs: [
          'Sometimes customers enjoy the first experience but feel like they have already seen everything. This can happen especially quickly with smaller businesses.',
          'For example, a customer visits a cafe with a small menu, orders the signature drink and most popular dessert, and leaves feeling like they have already tried the main things worth trying.',
          'That does not mean the business needs to constantly reinvent itself. Small changes can be enough:',
        ],
        list: [
          'Rotate one or two products',
          'Introduce seasonal items',
          'Run occasional collaborations',
          'Add limited-time bundles',
          'Hold small events',
          'Highlight products customers may have overlooked',
        ],
        after: [
          'The goal is to keep the core experience familiar while occasionally giving existing customers something new to explore.',
        ],
      },
      {
        title: 'The experience is inconsistent',
        paragraphs: [
          'Customers generally want to know what they can expect when they return.',
          'Imagine someone ordering the same coffee twice. The first time, it is excellent. The second time, it tastes noticeably different and takes 25 minutes to arrive.',
          'Consistency matters across areas such as:',
        ],
        list: [
          'Product quality',
          'Service',
          'Waiting times',
          'Pricing',
          'Cleanliness',
          'Availability',
          'Staff knowledge',
        ],
        after: [
          'The experience does not need to be perfect every time. But customers should not feel like they are taking a gamble each time they return.',
        ],
      },
      {
        title: 'Customers do not feel recognised',
        paragraphs: [
          'Smaller businesses have an advantage that large businesses sometimes struggle to replicate: familiarity.',
          'Imagine a customer who visits the same neighbourhood cafe every Saturday. On their fourth visit, the staff member asks, “Same iced latte today?”',
          'That tiny interaction can make the business feel much more personal.',
          'The same idea applies elsewhere. A barber might remember how a customer likes their hair cut, a pet groomer might remember a dog’s temperament, and a retailer might remember the style a regular customer usually prefers.',
          'Small gestures can include:',
        ],
        list: [
          'Remembering a customer’s preference',
          'Greeting them by name',
          'Acknowledging that they have visited before',
          'Making recommendations based on previous purchases',
          'Simply saying “welcome back”',
        ],
        after: ['Customers often return to places where they feel comfortable and recognised.'],
      },
      {
        title: 'Convenience wins',
        paragraphs: [
          'Sometimes the problem has little to do with the quality of the business. A competitor may simply be easier.',
          'Two cafes might serve equally good coffee, but one allows customers to order immediately while the other regularly has a 20-minute queue. Two salons might offer similar services, but one lets customers book online in 30 seconds while the other requires several messages back and forth.',
          'Friction can come from many places:',
        ],
        list: [
          'Long queues',
          'Complicated booking',
          'Slow replies',
          'Limited payment options',
          'Unclear opening hours',
          'Difficult ordering',
          'Frequent stock issues',
        ],
        after: [
          'Customers often choose the option that is easiest. You may not be able to change your location, but you can make the rest of the experience smoother.',
        ],
      },
      {
        title: 'Competitors give them a stronger reason to return',
        paragraphs: [
          'Customers are rarely choosing between one excellent business and one terrible one. They are usually choosing between several decent options.',
          'Imagine three cafes in the same neighbourhood. One has the best coffee, one has a loyalty card, and one changes its pastry selection every week. A customer may enjoy all three, but each gives them a different reason to return.',
          'When looking at competitors, pay attention to:',
        ],
        list: [
          'What customers repeatedly praise in reviews',
          'Their pricing',
          'Their promotions',
          'Their product range',
          'Their service experience',
          'How easy they make repeat purchases',
        ],
        after: [
          'The goal is not to copy them. It is to understand what customers in your market actually value.',
        ],
      },
      {
        title: 'You disappear after the first transaction',
        paragraphs: [
          'For many businesses, the customer relationship ends when payment is complete. The customer leaves, and there is no further interaction.',
          'Imagine someone visits a cafe once after seeing it on Instagram. They enjoy the experience but never follow the cafe. Six weeks later, the cafe launches a new dessert they would probably love, but the customer never hears about it.',
          'Businesses can stay visible through channels such as:',
        ],
        list: [
          'Social media',
          'Email',
          'Messaging',
          'Customer lists',
          'Loyalty programmes',
          'Events',
          'Google Business Profile updates',
        ],
        after: [
          'You do not need to contact customers constantly. Sometimes a simple, relevant reminder is enough to bring the business back to mind.',
        ],
      },
    ],
    conclusion: {
      title: 'The second visit matters',
      paragraphs: [
        'Turning a first-time customer into a loyal customer rarely happens immediately. A more useful first goal is simply to get them to come back once.',
        'After a few positive experiences, familiarity starts to build. Customers know what to expect, how the business works, and what they like. Choosing you again becomes easier.',
        'Instead of only asking how to attract more new customers, it is worth asking another question too:',
      ],
      closingCallout: 'What are we doing to make the second visit happen?',
    },
  },
  [articles[1].slug]: {
    ...articles[1],
    intro: [
      'Discounts are an easy way to attract attention, but they are also easy to overuse. If every attempt to bring customers back involves lowering prices, promotions can quickly eat into margins and train customers to wait for the next deal.',
      'Fortunately, repeat business does not have to depend entirely on discounts. Some of the strongest reasons customers return have very little to do with price.',
    ],
    sections: [
      {
        title: 'Give customers something they cannot easily replace',
        paragraphs: [
          'Customers are more likely to return when there is something specific they associate with your business.',
          'For a cafe, it might be a particular pastry that customers cannot find elsewhere. For a salon, it might be a stylist who understands exactly what the customer wants. For a retailer, it might be a carefully selected range of products that saves customers from having to search elsewhere.',
          'Look for clues in your own business:',
        ],
        list: [
          'Which products do customers repeatedly order?',
          'What gets mentioned most often in reviews?',
          'What do regular customers recommend to friends?',
          'What do customers specifically come back for?',
        ],
        after: [
          'If a cafe repeatedly hears “I came back for the matcha tiramisu”, that is useful information. The business may already have one of its strongest retention drivers.',
        ],
      },
      {
        title: 'Give customers something new to come back for',
        paragraphs: [
          'Customers like familiarity, but they also respond to novelty. A business does not need to reinvent itself every month, but small changes can keep the experience feeling fresh.',
          'A cafe could keep its core menu unchanged while rotating one drink or dessert. A retailer could introduce a new collection every few weeks. A fitness studio could add an occasional themed class.',
          'Simple ways to create novelty include:',
        ],
        list: [
          'Seasonal products',
          'Limited-time items',
          'New services',
          'Small collaborations',
          'Special events',
          'Rotating bundles',
        ],
        after: [
          'You do not need constant change. You just need enough to give previous customers another reason to look again.',
        ],
      },
      {
        title: 'Make regular customers feel like regulars',
        paragraphs: [
          'Recognition can be surprisingly powerful.',
          'Imagine two coffee shops. At one, every visit feels completely transactional. At the other, the staff recognise the customer and ask, “Still having the oat latte?”',
          'The product may be similar, but the second experience feels more personal.',
          'Small gestures can make a difference:',
        ],
        list: [
          'Remembering a preference',
          'Greeting someone by name',
          'Acknowledging a previous purchase',
          'Making a relevant recommendation',
          'Saying “welcome back”',
        ],
        after: ['Customers often return to places where they feel comfortable and known.'],
      },
      {
        title: 'Become part of a routine',
        paragraphs: [
          'Some of the strongest businesses become part of customers’ habits.',
          'A cafe might become someone’s Monday morning coffee stop. A barber might become the place they visit every four weeks. A gym might become part of their after-work routine.',
          'Think about when customers naturally need your product or service again, then make returning easy.',
          'That could mean:',
        ],
        list: [
          'Offering easy rebooking',
          'Sending a useful reminder',
          'Allowing faster repeat ordering',
          'Saving customer preferences',
          'Creating prepaid packages',
          'Making opening hours fit common customer routines',
        ],
        after: [
          'The easier the repeat behaviour becomes, the more likely it is to turn into a habit.',
        ],
      },
      {
        title: 'Use rewards selectively',
        paragraphs: [
          'Rewards can still be useful. The key is using them to encourage a specific action rather than simply discounting every transaction.',
          'For example, instead of giving a cafe customer 10% off the purchase they are already making, a business could make the reward apply to their next visit.',
          'Rewards could encourage:',
        ],
        list: [
          'A second visit',
          'A repeat booking',
          'A certain number of purchases',
          'Referring a friend',
          'Visiting during quieter periods',
          'Trying a new product or service',
        ],
        after: [
          'They also do not always need to be cash discounts. A free upgrade, complimentary item, small perk, or priority access can feel valuable without significantly reducing the value of the sale.',
        ],
      },
      {
        title: 'Keep customers aware of what is happening',
        paragraphs: [
          'Customers cannot return for something they do not know about.',
          'Imagine a cafe launches a new weekend brunch menu. The regulars who happen to walk past will see it, but everyone else may never know.',
          'Depending on the business, updates could be shared through:',
        ],
        list: [
          'Social media',
          'Email',
          'WhatsApp or other messaging',
          'In-store signage',
          'Google Business Profile',
          'A customer list',
        ],
        after: [
          'The goal is not to message customers constantly. It is to occasionally give them something genuinely worth knowing.',
          '“New strawberry shortcake available this weekend” is much more useful than repeatedly sending generic messages asking customers to come back.',
        ],
      },
      {
        title: 'Ask your regulars why they return',
        paragraphs: [
          'Businesses sometimes assume they know why customers keep coming back, but they may be wrong.',
          'A cafe owner may think people return because of the coffee, while regulars actually value the quiet seating and reliable Wi-Fi. A salon may think price is its biggest advantage, while customers actually care most about how comfortable the staff make them feel.',
          'You do not need a formal survey. Ask a few regular customers simple questions such as:',
        ],
        list: [
          'What keeps bringing you back?',
          'What’s your favourite thing about this place?',
          'Is there anything we do better than similar businesses?',
          'What would you miss if we were not here?',
        ],
        after: [
          'Patterns in their answers can reveal what is actually driving repeat business.',
        ],
      },
      {
        title: 'Remove small frustrations',
        paragraphs: [
          'Sometimes improving retention is less about adding something new and more about removing reasons not to return.',
          'A cafe may have excellent food but an ordering system that regularly creates a 30-minute wait. A salon may provide great service but make booking unnecessarily difficult. A retailer may have good products but frequently run out of popular items.',
          'Common sources of friction include:',
        ],
        list: [
          'Long waiting times',
          'Confusing booking processes',
          'Slow replies',
          'Inconsistent opening hours',
          'Difficult payment options',
          'Poor communication',
          'Unclear pricing',
          'Frequent stock shortages',
        ],
        after: [
          'Customers may never complain directly. They may simply choose somewhere else next time.',
          'Pay attention to recurring questions, customer reviews, and staff feedback. If the same frustration keeps appearing, fixing it may do more for retention than launching another promotion.',
        ],
      },
    ],
    conclusion: {
      title: 'Repeat business is usually built through many small reasons',
      paragraphs: [
        'There is rarely one tactic that creates loyal customers. Repeat business usually comes from a combination of things: a product customers genuinely like, a consistent experience, convenience, recognition, something new to discover, and a reason to remember the business.',
        'Discounts can support that, but they should not have to do all the work. The goal is to make returning feel like the natural choice, not simply the cheapest one.',
      ],
    },
  },
};

function usePageMetadata(title, description) {
  useEffect(() => {
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute('content') || '';
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', description);
    return () => {
      document.title = 'LoyalQR.sg';
      meta.setAttribute('content', previousDescription);
    };
  }, [description, title]);
}

function Arrow() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="M4 10h11M11 6l4 4-4 4" />
    </svg>
  );
}

export function ResourcesPage() {
  usePageMetadata(
    'Resources for building repeat business | LoyalQR.sg',
    'Practical guides for local business owners who want to turn more first visits into repeat business.'
  );

  return (
    <main className="resources-page">
      <PublicHeader currentPage="resources" />
      <section className="resources-hero">
        <h1>Practical ideas for building repeat business.</h1>
        <p>
          Useful guides on customer retention, loyalty, and creating experiences that
          give people a reason to come back.
        </p>
      </section>
      <section className="resources-list" aria-label="All resources">
        {articles.map((article) => (
          <article className="resource-row" key={article.slug}>
            <div>
              <h2>{article.title}</h2>
              <p>{article.summary}</p>
            </div>
            <Link to={`/resources/${article.slug}`} aria-label={`Read ${article.title}`}>
              Read guide
              <Arrow />
            </Link>
          </article>
        ))}
      </section>
      <section className="resources-note">
        <div>
          <h2>A simple way to give customers a reason to return</h2>
          <p>
            LoyalQR helps businesses create a next-visit voucher customers can claim
            with a QR code — no customer app required.
          </p>
        </div>
        <Link to="/contact">See if LoyalQR fits your business</Link>
      </section>
      <PublicFooter />
    </main>
  );
}

function ArticleSection({ section, index }) {
  const sectionId = `section-${index + 1}`;
  return (
    <section id={sectionId} className="resource-article-section">
      <h2><span aria-hidden="true">{index + 1}.</span> {section.title}</h2>
      {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      {section.list && <ul>{section.list.map((item) => <li key={item}>{item}</li>)}</ul>}
      {section.callout && <blockquote>{section.callout}</blockquote>}
      {section.after?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
    </section>
  );
}

export function ResourceArticlePage({ slug }) {
  const article = articleContent[slug];
  usePageMetadata(
    article ? `${article.title} | LoyalQR.sg` : 'Resource not found | LoyalQR.sg',
    article?.summary || 'The requested LoyalQR resource could not be found.'
  );
  if (!article) return null;

  return (
    <main className="resources-page article-page">
      <PublicHeader currentPage="resources" />
      <div className="article-back-wrap">
        <Link className="article-back" to="/resources">
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path d="m11.75 4.75-5.25 5.25 5.25 5.25M7 10h7" />
          </svg>
          All resources
        </Link>
      </div>
      <article className="resource-article">
        <header className="resource-article-header">
          <h1>{article.title}</h1>
          <p>{article.summary}</p>
        </header>
        <div className="resource-article-layout">
          <aside className="article-contents" aria-label="Article contents">
            <strong>In this guide</strong>
            <ol>
              {article.sections.map((section, index) => (
                <li key={section.title}>
                  <a href={`#section-${index + 1}`}>{section.title}</a>
                </li>
              ))}
            </ol>
          </aside>
          <div className="resource-article-body">
            <div className="resource-article-intro">
              {article.intro.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </div>
            {article.sections.map((section, index) => (
              <ArticleSection section={section} index={index} key={section.title} />
            ))}
            <section className="resource-conclusion">
              <h2>{article.conclusion.title}</h2>
              {article.conclusion.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              {article.conclusion.closingCallout && <blockquote>{article.conclusion.closingCallout}</blockquote>}
            </section>
            <nav className="article-next" aria-label="Continue reading">
              <span>Continue reading</span>
              {articles.filter((item) => item.slug !== slug).map((item) => (
                <Link to={`/resources/${item.slug}`} key={item.slug}>
                  {item.title}
                  <Arrow />
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </article>
      <PublicFooter />
    </main>
  );
}

export function isResourceArticle(slug) {
  return Boolean(articleContent[slug]);
}
